import React from 'react'
import { OrderStatus } from '../../domain/entities/Order'

interface OrderStatusStepperProps {
  currentStatus: OrderStatus
  hasGrievance?: boolean
}

const steps: { status: OrderStatus; label: string }[] = [
  { status: 'indent_raised', label: 'Indent Placed' },
  { status: 'order_placed', label: 'Order Placed' },
  { status: 'payment_received', label: 'Payment Received' },
  { status: 'order_confirmed', label: 'Order Confirmed' },
  { status: 'in_progress', label: 'In Progress' },
  { status: 'dispatched', label: 'Dispatched' },
  { status: 'delivered', label: 'Delivered' },
]

const getNodeClasses = (isCompleted: boolean, isActive: boolean, isGrievanceActive: boolean): string => {
  if (isCompleted) {
    return 'bg-status-active border-status-active text-white'
  }

  if (isActive) {
    return isGrievanceActive
      ? 'bg-danger border-danger text-white font-black'
      : 'bg-primary border-primary text-slate-950 font-black'
  }

  return 'bg-surface border-border text-text-secondary'
}

const getLabelClasses = (isActive: boolean, isGrievanceActive: boolean, isCompleted: boolean): string => {
  if (isActive) {
    return isGrievanceActive ? 'text-danger' : 'text-primary'
  }

  if (isCompleted) {
    return 'text-text-primary'
  }

  return 'text-text-secondary'
}

export function OrderStatusStepper({ currentStatus, hasGrievance = false }: OrderStatusStepperProps) {
  const activeIdx = steps.findIndex((s) => s.status === currentStatus)
  const isGrievanceActive = hasGrievance && currentStatus === 'delivered'

  return (
    <div className="w-full py-4 select-none">
      {/* Mobile Vertical Stepper */}
      <div className="md:hidden flex flex-col space-y-4 px-4 py-2">
        {steps.map((step, idx) => {
          const isCompleted = idx < activeIdx
          const isActive = idx === activeIdx

          return (
            <div key={step.status} className="flex items-start gap-3 relative">
              {/* Connecting vertical line */}
              {idx < steps.length - 1 && (
                <div
                  className={`absolute left-4 top-8 bottom-0 w-[2px] -ml-[1px] ${
                    isCompleted ? 'bg-status-active' : 'bg-border'
                  }`}
                  style={{ height: 'calc(100% + 1rem)' }}
                />
              )}

              {/* Node indicator */}
              <div
                className={`h-8 w-8 rounded-full border-2 flex items-center justify-center font-bold text-xs transition-colors duration-300 z-10 flex-shrink-0 ${getNodeClasses(
                  isCompleted,
                  isActive,
                  isGrievanceActive,
                )}`}
              >
                {isCompleted ? (
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  idx + 1
                )}
              </div>

              {/* Step Label details */}
              <div className="pt-1.5">
                <span
                  className={`text-xs font-semibold block transition-colors duration-300 ${getLabelClasses(
                    isActive,
                    isGrievanceActive,
                    isCompleted,
                  )}`}
                >
                  {step.label}
                </span>
              </div>
            </div>
          )
        })}
      </div>

      {/* Desktop Horizontal Stepper */}
      <div className="hidden md:block overflow-x-auto">
        <div className="flex items-center min-w-[600px] justify-between px-4">
          {steps.map((step, idx) => {
            const isCompleted = idx < activeIdx
            const isActive = idx === activeIdx

            return (
              <React.Fragment key={step.status}>
                {/* Stepper Node */}
                <div className="flex flex-col items-center flex-1 relative">
                  <div
                    className={`h-8 w-8 rounded-full border-2 flex items-center justify-center font-bold text-xs transition-colors duration-300 z-10 ${getNodeClasses(
                      isCompleted,
                      isActive,
                      isGrievanceActive,
                    )}`}
                  >
                    {isCompleted ? (
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      idx + 1
                    )}
                  </div>
                  <span
                    className={`mt-2 text-xs font-semibold text-center whitespace-nowrap transition-colors duration-300 ${getLabelClasses(
                      isActive,
                      isGrievanceActive,
                      isCompleted,
                    )}`}
                  >
                    {step.label}
                  </span>
                </div>

                {/* Connecting line */}
                {idx < steps.length - 1 && (
                  <div className="flex-1 h-[2px] bg-border relative -top-3.5 mx-2 min-w-[20px]">
                    <div
                      className="absolute inset-y-0 left-0 bg-status-active transition-all duration-500 ease-out"
                      style={{ width: isCompleted ? '100%' : '0%' }}
                    />
                  </div>
                )}
              </React.Fragment>
            )
          })}
        </div>
      </div>
    </div>
  )
}
